import React, { useState, useEffect } from 'react';
import {
  Repeat, Download, Copy, Check, FileCode2, Loader2, AlertTriangle, Server
} from 'lucide-react';

const REF_PATH = '/reference/SyncResponseRegistry.java';

const STEPS = [
  ['park(correlationId)', 'Before sending anything to the hub, register the pending operation and capture the current tenant context. The correlationId is the ID that will appear in the hub callback URL.'],
  ['registerResourceKey(...)', 'Map a resource key — MSISDN, quoteId, or transferId — to the correlationId, so the callback handler can restore tenant context from the callback URL alone.'],
  ['Send request → 202 Accepted', 'POST to the hub. It returns 202 immediately; the real result is not in this response.'],
  ['await(correlationId, timeout)', 'Block the CBS thread until the callback delivers a result or the timeout fires.'],
  ['Hub callback → complete(...)', 'Seconds later the hub calls back (PUT /…/{id}). The callback handler resolves the result and signals the parked operation.'],
  ['await unblocks → return to CBS', 'The blocked thread wakes with the result and returns a single synchronous response to the CBS.'],
];

const BACKENDS = [
  ['InMemoryRegistryBackend', 'SINGLE_TENANT', 'CompletableFuture in a map. Zero dependencies, lowest latency. Needs sticky-session load balancing if you run more than one instance.'],
  ['RedisRegistryBackend', 'MULTI_TENANT', 'Redis BLPOP/RPUSH rendezvous. Any instance can handle any callback — true horizontal scaling, no sticky sessions.'],
];

const PITFALLS = [
  ['Callback before await (the race)', 'The hub can call back before your CBS thread reaches await(). The backend must store the result so a late await() still resolves — never assume park → await → callback ordering.'],
  ['Sticky sessions when scaling in-memory', 'With the in-memory backend, the instance that parked must be the one that receives the callback. Scale out only with sticky routing, or switch to the Redis backend.'],
  ['Timeout shorter than the hub', 'await() must allow more time than the hub takes to call back. Party lookup, quote, and transfer each need their own timeout — a too-short value returns an error while the payment still completes at the hub.'],
  ['Expecting a second transfer callback', 'As payer, a transfer ends with one callback: PUT /transfers with the final state. A PATCH /transfers commit notification goes only to the payee, and only when the payee answered RESERVED. If you are the payee and answer RESERVED, keep enough context to process that PATCH when it arrives.'],
  ['Duplicate / retried callbacks', 'Hubs retry. complete() returning false (no waiter found) is normal for a duplicate — handle it idempotently rather than treating it as an error.'],
];

export default function AsyncCallbacksGuide() {
  const [code, setCode]     = useState('');
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(REF_PATH)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(t => { setCode(t); setLoad(false); })
      .catch(e => { setError(e.message); setLoad(false); });
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Repeat className="w-6 h-6 text-primary-600" />
          Async Callbacks &amp; Correlation
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          FSPIOP is asynchronous — you <span className="font-mono text-xs">POST</span> and get a
          <span className="font-mono text-xs"> 202 Accepted</span>, then the hub calls back with the
          real result seconds later. This is how the connector correlates that callback to the
          original request and turns it into a single synchronous response for the CBS.
        </p>
      </div>

      {/* Sequence diagram */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">The async flow</h2>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
          <svg viewBox="0 0 680 250" width="100%" role="img"
               aria-label="Sequence: CBS calls the connector, which parks, posts to the hub, gets 202, awaits, the hub calls back, the connector completes and returns synchronously to the CBS">
            {/* lifelines */}
            {[['CBS', 110], ['Connector', 340], ['Mojaloop Hub', 570]].map(([n, x]) => (
              <g key={n}>
                <rect x={x - 64} y="8" width="128" height="32" rx="8" fill="#fff" stroke="#cbd5e1" strokeWidth="1" />
                <text x={x} y="29" textAnchor="middle" fontSize="13" fontFamily="system-ui" fill="#0f172a">{n}</text>
                <line x1={x} y1="40" x2={x} y2="238" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 4" />
              </g>
            ))}
            {/* messages */}
            {[
              [110, 340, 64, 'request (sync)', '#2563eb'],
              [340, 340, 92, 'park + registerResourceKey', '#64748b'],
              [340, 570, 120, 'POST /transfers', '#2563eb'],
              [570, 340, 148, '202 Accepted', '#94a3b8'],
              [340, 340, 176, 'await(correlationId, timeout)', '#d97706'],
              [570, 340, 204, 'PUT /transfers/{id}  → complete()', '#16a34a'],
              [340, 110, 232, 'result (sync)', '#16a34a'],
            ].map(([x1, x2, y, label, color], i) => {
              if (x1 === x2) {
                return (
                  <g key={i}>
                    <rect x={x1 + 6} y={y - 11} width="6" height="14" fill="none" stroke={color} strokeWidth="1.2" />
                    <text x={x1 + 18} y={y} fontSize="11" fontFamily="ui-monospace, monospace" fill={color}>{label}</text>
                  </g>
                );
              }
              const dir = x2 > x1 ? 1 : -1;
              const hx = x2 - dir * 7;
              return (
                <g key={i}>
                  <line x1={x1} y1={y} x2={hx} y2={y} stroke={color} strokeWidth="1.5" />
                  <path d={`M${x2},${y} L${hx},${y - 4} L${hx},${y + 4} Z`} fill={color} />
                  <text x={(x1 + x2) / 2} y={y - 5} textAnchor="middle" fontSize="11" fontFamily="ui-monospace, monospace" fill={color}>{label}</text>
                </g>
              );
            })}
          </svg>
        </div>
      </section>

      {/* Correlation pattern steps */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">The correlation pattern</h2>
        <ol className="space-y-2">
          {STEPS.map(([title, desc], i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="text-sm font-medium text-gray-900 font-mono">{title}</p>
                <p className="text-sm text-gray-600">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Backends */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Server className="w-5 h-5 text-gray-400" /> Backend strategy
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          The same public API runs over two backends. Callers never know which is active.
        </p>
        <div className="space-y-2">
          {BACKENDS.map(([name, mode, desc]) => (
            <div key={name} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-sm text-gray-900">{name}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{mode}</span>
              </div>
              <p className="text-sm text-gray-600">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pitfalls */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Common pitfalls
        </h2>
        <div className="space-y-2">
          {PITFALLS.map(([title, desc]) => (
            <div key={title} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-900">{title}</p>
              <p className="text-sm text-amber-800 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reference */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileCode2 className="w-5 h-5 text-gray-400" /> Reference implementation
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={copyCode} disabled={!code}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a href={REF_PATH} download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
              <Download className="w-4 h-4" /> Download .java
            </a>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          <span className="font-mono text-xs">SyncResponseRegistry.java</span> — the park / await /
          complete bridge. Note how it captures tenant context at park time and delegates storage to
          a swappable backend (in-memory or Redis).
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reference source…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            Could not load reference source: {error}
          </div>
        )}
        {!loading && !error && (
          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-[480px] leading-relaxed">
            {code}
          </pre>
        )}
      </section>
    </div>
  );
}
